import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/perms";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { PackageCheck, CheckCircle2, CircleSlash, AlertTriangle, Clock, Boxes, ChevronRight } from "lucide-react";
import { unitNoun, pkgCategory } from "@/lib/packageUnit";

/* Pawkt design tokens (shared with the 360 surfaces / Command Center). */
const C = {
  surface: "#FFFFFF", elevated: "#F5F1EA", primary: "#7C5CFC", primaryDeep: "#5837D8",
  primarySoft: "#EEE9FE", text: "#1F1B18", textSec: "#7B6D62", textDis: "#B5A89D",
  border: "#EEE8DD", warn: "#B8791F", warnSoft: "#FBF3E8", danger: "#B4453A", dangerSoft: "#FBECEA",
  ok: "#2E7D5B", okSoft: "#E8F4EE",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

/* DISPLAY-ONLY state. Mirrors the backend runtime rule (_pkg_expired = expiry_date<=now)
   using server-provided fields; it never recomputes credits or eligibility. */
export function pkgState(p) {
  const exp = p?.expiry_date ? new Date(p.expiry_date) : null;
  const expired = p?.status === "expired" || (exp && !Number.isNaN(exp.getTime()) && exp <= new Date());
  const remaining = Number(p?.remaining_credits ?? 0);
  if (expired) return "expired";
  if (p?.status === "exhausted" || remaining <= 0) return "exhausted";
  if (remaining <= 2) return "low";
  return "active";
}

const serviceNames = (p, svcMap) => {
  const ids = p?.service_ids || [];
  if (!ids.length) return ["All services"];
  return ids.map((id) => svcMap[id] || "Service");
};

/* ---------------------------------------------------------------- CoverageBadge
   Pure presentational. NEVER claims coverage on its own — the caller passes the
   server-authoritative covered_qty (from POS preview / invoice line). */
export function CoverageBadge({ qty = 0, coveredQty = 0, status, category = "", testid }) {
  let cfg;
  if (status === "expired") cfg = { icon: Clock, label: "Package expired", bg: C.dangerSoft, fg: C.danger };
  else if (status === "exhausted") cfg = { icon: CircleSlash, label: "Package exhausted", bg: C.warnSoft, fg: C.warn };
  else if (coveredQty <= 0) cfg = { icon: CircleSlash, label: "Not covered", bg: C.elevated, fg: C.textSec };
  else if (coveredQty >= qty) cfg = { icon: CheckCircle2, label: `Covered by package · ${coveredQty} ${unitNoun(category, coveredQty)} · ₹0`, bg: C.okSoft, fg: C.ok };
  else cfg = { icon: PackageCheck, label: `Partially covered · ${coveredQty} ${unitNoun(category, coveredQty)} covered · ${qty - coveredQty} billed`, bg: C.primarySoft, fg: C.primaryDeep };
  const Icon = cfg.icon;
  return (
    <span data-testid={testid || "coverage-badge"} role="status" aria-label={cfg.label}
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-bold"
      style={{ background: cfg.bg, color: cfg.fg }}>
      <Icon size={13} aria-hidden /> {cfg.label}
    </span>
  );
}

const STATE_UI = {
  active: { border: C.border, chipBg: C.primarySoft, chipFg: C.primaryDeep, chip: "Active" },
  low: { border: C.warn, chipBg: C.warnSoft, chipFg: C.warn, chip: "Low balance" },
  exhausted: { border: C.border, chipBg: C.elevated, chipFg: C.textSec, chip: "Exhausted" },
  expired: { border: C.danger, chipBg: C.dangerSoft, chipFg: C.danger, chip: "Expired" },
};

function PkgCard({ p, svcMap, svcCatMap = {}, onOpen }) {
  const st = pkgState(p);
  const ui = STATE_UI[st];
  const total = Number(p.total_credits ?? 0);
  const used = Number(p.used_credits ?? 0);
  const remaining = Number(p.remaining_credits ?? 0);
  const names = serviceNames(p, svcMap);
  const cat = pkgCategory(p, svcCatMap);
  const unusable = st === "expired" || st === "exhausted";
  return (
    <button type="button" onClick={() => onOpen(p)} data-testid={`pkg-card-${p.id}`}
      className="w-full text-left rounded-[18px] p-4 transition-colors hover:bg-[#FBF9F5] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7C5CFC]"
      style={{ background: C.surface, border: `1px solid ${ui.border}`, borderLeft: `4px solid ${st === "expired" ? C.danger : st === "low" ? C.warn : C.primary}` }}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex items-center gap-2">
          <span className="shrink-0 grid place-items-center w-8 h-8 rounded-[10px]" style={{ background: C.primarySoft, color: C.primaryDeep }}><Boxes size={16} aria-hidden /></span>
          <div className="min-w-0">
            <p className="font-bold text-sm truncate" style={{ color: C.text }} data-testid={`pkg-name-${p.id}`}>{p.plan_name || "Package"}</p>
            <p className="text-[12px]" style={{ color: C.textSec }}>{names.join(" · ")}</p>
          </div>
        </div>
        <span className="shrink-0 rounded-full px-2 py-0.5 text-[11px] font-bold" style={{ background: ui.chipBg, color: ui.chipFg }} data-testid={`pkg-state-${p.id}`}>{ui.chip}</span>
      </div>
      <div className="mt-3 flex items-end justify-between gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: C.textSec }}>Remaining</p>
          <p className="font-display font-extrabold leading-none" style={{ fontSize: 26, color: unusable ? C.textDis : C.text }} data-testid={`pkg-remaining-${p.id}`}>
            {unusable ? 0 : remaining}<span className="ml-1 text-[13px] font-semibold" style={{ color: C.textSec }}>{unitNoun(cat, unusable ? 0 : remaining)}</span>
          </p>
        </div>
        <div className="text-right">
          <p className="text-[12px]" style={{ color: C.textSec }}>Used {used} / {total}</p>
          <p className="text-[12px]" style={{ color: st === "expired" ? C.danger : C.textSec }}>{st === "expired" ? "Expired" : "Expires"} {fmtDate(p.expiry_date)}</p>
        </div>
      </div>
      {/* usage bar (display mirror of used/total) */}
      <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: C.elevated }}>
        <div style={{ width: `${total ? Math.min(100, (used / total) * 100) : 0}%`, background: st === "expired" ? C.danger : st === "low" ? C.warn : C.primary }} className="h-full" />
      </div>
    </button>
  );
}

/* ---------------------------------------------------------------- PackageBalanceWidget
   Server-authoritative: reads GET /api/packages?customer_id=. No credit math is done here. */
export function PackageBalanceWidget({ customerId, petId, title = "Packages", compact = false }) {
  const { user } = useAuth();
  const [detail, setDetail] = useState(null);
  const canView = hasPerm(user, "customers.view");
  const { data, isLoading, isError } = useQuery({
    queryKey: ["packages", user?.tenant_id || user?.tenant?.id, customerId],
    queryFn: async () => (await api.get("/packages", { params: { customer_id: customerId } })).data,
    enabled: !!customerId && canView,
  });
  const { data: services } = useQuery({
    queryKey: ["services"], queryFn: async () => (await api.get("/services")).data, enabled: canView,
  });
  if (!canView || !customerId) return null;
  const svcMap = Object.fromEntries((services || []).map((s) => [s.id, s.name]));
  const svcCatMap = Object.fromEntries((services || []).map((s) => [s.id, s.category]));
  const pkgs = (data || []).slice().sort((a, b) => {
    const rank = { active: 0, low: 1, exhausted: 2, expired: 3 };
    return (rank[pkgState(a)] - rank[pkgState(b)]) || (new Date(a.expiry_date) - new Date(b.expiry_date));
  });

  return (
    <section data-testid="package-balance-widget">
      <div className="flex items-center gap-2 mb-2.5">
        <PackageCheck size={16} style={{ color: C.primaryDeep }} aria-hidden />
        <h3 className="font-bold text-sm" style={{ color: C.text }}>{title}</h3>
        {petId && pkgs.length > 0 && (
          <span className="text-[11px] font-semibold rounded-full px-2 py-0.5" style={{ background: C.elevated, color: C.textSec }}>Customer-level coverage</span>
        )}
      </div>
      {isLoading ? (
        <div className="rounded-[18px] p-5 animate-pulse" style={{ background: C.surface, border: `1px solid ${C.border}` }} data-testid="pkg-loading">
          <div className="h-4 w-1/3 rounded mb-3" style={{ background: C.elevated }} />
          <div className="h-7 w-1/4 rounded" style={{ background: C.elevated }} />
        </div>
      ) : isError ? (
        <div className="rounded-[18px] p-4 text-[13px]" style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.textSec }} data-testid="pkg-error">
          Couldn’t load package coverage. Refresh to try again.
        </div>
      ) : pkgs.length === 0 ? (
        <div className="rounded-[18px] p-4 flex items-center gap-2.5 text-[13px]" style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.textSec }} data-testid="pkg-empty">
          <CircleSlash size={16} aria-hidden style={{ color: C.textDis }} /> No active package coverage
        </div>
      ) : (
        <div className={compact ? "space-y-2.5" : "grid gap-3 sm:grid-cols-2"} data-testid="pkg-list">
          {pkgs.map((p) => <PkgCard key={p.id} p={p} svcMap={svcMap} svcCatMap={svcCatMap} onOpen={setDetail} />)}
        </div>
      )}
      <PackageDetailDrawer pkg={detail} open={!!detail} onOpenChange={(o) => !o && setDetail(null)} svcMap={svcMap} svcCatMap={svcCatMap} />
    </section>
  );
}

/* ---------------------------------------------------------------- PackageDetailDrawer
   Read-only. Pulls GET /api/packages/{id}/usage (canonical package doc + existing ledger). */
export function PackageDetailDrawer({ pkg, open, onOpenChange, svcMap = {}, svcCatMap = {} }) {
  const { data } = useQuery({
    queryKey: ["package-usage", pkg?.id],
    queryFn: async () => (await api.get(`/packages/${pkg.id}/usage`)).data,
    enabled: !!pkg?.id && open,
  });
  if (!pkg) return null;
  const p = data?.package || pkg;
  const st = pkgState(p);
  const names = serviceNames(p, svcMap);
  const cat = pkgCategory(p, svcCatMap);
  const ledger = data?.ledger || [];
  const Row = ({ label, value }) => (
    <div className="flex items-center justify-between gap-3 py-2 border-b" style={{ borderColor: C.border }}>
      <span className="text-[13px]" style={{ color: C.textSec }}>{label}</span>
      <span className="text-[13px] font-semibold text-right" style={{ color: C.text }}>{value}</span>
    </div>
  );
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="bg-white w-full sm:max-w-md overflow-y-auto" data-testid="pkg-detail-drawer">
        <SheetHeader><SheetTitle className="font-display text-lg" style={{ color: C.text }}>{p.plan_name || "Package"}</SheetTitle></SheetHeader>
        <div className="mt-4 rounded-[14px] p-4" style={{ background: C.elevated }}>
          <div className="flex items-end justify-between">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: C.textSec }}>Remaining</p>
              <p className="font-display font-extrabold leading-none" style={{ fontSize: 30, color: C.text }} data-testid="drawer-remaining">{Number(p.remaining_credits ?? 0)}<span className="ml-1.5 text-[13px] font-semibold" style={{ color: C.textSec }}>{unitNoun(cat, Number(p.remaining_credits ?? 0))}</span></p>
            </div>
            <span className="rounded-full px-2.5 py-1 text-[12px] font-bold" style={{ background: STATE_UI[st].chipBg, color: STATE_UI[st].chipFg }}>{STATE_UI[st].chip}</span>
          </div>
        </div>
        <div className="mt-4">
          <Row label={`Included ${unitNoun(cat, 2)}`} value={Number(p.total_credits ?? 0)} />
          <Row label={`Used ${unitNoun(cat, 2)}`} value={Number(p.used_credits ?? 0)} />
          <Row label={`Remaining ${unitNoun(cat, 2)}`} value={Number(p.remaining_credits ?? 0)} />
          <Row label="Eligible services" value={names.join(", ")} />
          <Row label="Purchased" value={fmtDate(p.created_at)} />
          <Row label="Expires" value={fmtDate(p.expiry_date)} />
          <Row label="Status" value={STATE_UI[st].chip} />
        </div>
        {ledger.length > 0 && (
          <div className="mt-5">
            <p className="text-[11px] font-bold uppercase tracking-wide mb-2" style={{ color: C.textSec }}>Recent usage</p>
            <ul className="space-y-1.5" data-testid="drawer-ledger">
              {ledger.map((l) => (
                <li key={l.id} className="flex items-center justify-between gap-3 text-[13px]">
                  <span className="flex items-center gap-2" style={{ color: C.text }}>
                    <ChevronRight size={13} aria-hidden style={{ color: C.primary }} />
                    {l.action === "restore" ? "Restored" : "Used"} {l.quantity} {unitNoun(cat, l.quantity)}
                  </span>
                  <span style={{ color: C.textSec }}>{fmtDate(l.created_at)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

export { C as PKG_TOKENS };
